import { Editor, Node, Operation, Path, Point, Range, TextUnit, Transforms } from 'slate';
import { CustomEditor, CustomElement } from 'slate/types/slate';
import { isTag } from './utils/navigation';
import { stripDuplicateTagPlacement } from './utils/duplicates';
import { relinkPredecessor } from './utils/blockTagging';
import { settleSplitFootnotes, settleSplitTags, tagsAtBreak, TagSplitResult } from './utils/split';
import { TagElement } from './components/TagElement';
import { WithTagOptions } from './types';
import { tagSplitHandlers } from './splitHandlers';
import { collapsedDeletionRange, removedTagIds, tagIdsIn } from './utils/removalWarning';

const PLACEMENT_OPERATIONS = new Set(['insert_node', 'split_node', 'merge_node', 'set_node', 'move_node']);

export const withTag =
  (options: WithTagOptions = {}) =>
  (editor: CustomEditor): CustomEditor => {
    const { isInline, insertText, insertBreak, normalizeNode, deleteBackward, deleteForward, deleteFragment, renderElement } = editor;
    const { isPDF } = options;
    let splittingTags = false;
    let duplicatePlacementChecked = false;
    const mayHaveDuplicatePlacement = () => !duplicatePlacementChecked || editor.operations.some((op: Operation) => PLACEMENT_OPERATIONS.has(op.type));

    editor.isInline = (element: CustomElement) => (isTag(element) || element.type === 'exclude' || element.type === 'footnote' ? true : isInline(element));

    const warnIfRemoved = (candidates: Set<string>) => {
      const [removed] = removedTagIds(editor, candidates);
      if (removed) editor.__onTagTextDeleted?.(removed);
    };

    const deleteCollapsed = (direction: 'backward' | 'forward', unit: TextUnit, remove: () => void) => {
      const { selection } = editor;
      if (!selection || !Range.isCollapsed(selection)) {
        remove();
        return;
      }
      const tagEntry = Editor.nodes(editor, { at: selection, match: isTag }).next().value;
      if (tagEntry && Node.string(tagEntry[0]).length === 1 && editor.__onTagAboutToBeEmpty) {
        editor.__onTagAboutToBeEmpty(tagEntry[0].tagId);
        return;
      }
      const candidates = tagIdsIn(editor, collapsedDeletionRange(editor, selection.anchor, direction, unit));
      remove();
      warnIfRemoved(candidates);
    };

    editor.deleteBackward = (unit) => deleteCollapsed('backward', unit, () => deleteBackward(unit));

    editor.deleteForward = (unit) => deleteCollapsed('forward', unit, () => deleteForward(unit));

    editor.deleteFragment = (direction) => {
      const candidates = editor.selection && !Range.isCollapsed(editor.selection) ? tagIdsIn(editor, editor.selection) : new Set<string>();
      deleteFragment(direction);
      warnIfRemoved(candidates);
    };

    editor.insertText = (text) => {
      const { selection } = editor;
      if (selection && Range.isExpanded(selection)) {
        const candidates = tagIdsIn(editor, selection);
        insertText(text);
        warnIfRemoved(candidates);
        return;
      }
      if (selection && Range.isCollapsed(selection)) {
        const [tagEntry] = Editor.nodes(editor, { match: isTag });
        if (tagEntry) {
          const [, tagPath] = tagEntry;
          const end = Editor.end(editor, tagPath);
          if (Point.equals(selection.anchor, end)) {
            const after = Editor.after(editor, tagPath);
            if (after) {
              Transforms.select(editor, after);
            }
          }
        }
      }
      insertText(text);
    };

    editor.insertBreak = () => {
      const tags = tagsAtBreak(editor);
      if (tags.inline.length === 0 && tags.block.length === 0 && tags.footnotes.length === 0) {
        insertBreak();
        return;
      }
      splittingTags = true;
      try {
        insertBreak();
      } finally {
        splittingTags = false;
      }
      const splitHandlers = tagSplitHandlers(editor);
      let result: TagSplitResult = { splits: [], blocked: [] };
      Editor.withoutNormalizing(editor, () => {
        result = settleSplitTags(editor, tags, (tagId) => (splitHandlers ? splitHandlers.continuationBlocker(tagId) : 'unsupported'));
        settleSplitFootnotes(editor, tags.footnotes);
        while (stripDuplicateTagPlacement(editor));
      });
      if (result.splits.length > 0 || result.blocked.length > 0) splitHandlers?.onTagSplit(result);
    };

    editor.normalizeNode = (entry) => {
      const [node, path] = entry;

      if (Editor.isEditor(node) && !splittingTags && mayHaveDuplicatePlacement()) {
        duplicatePlacementChecked = true;
        if (stripDuplicateTagPlacement(editor)) return;
      }

      if (isTag(node)) {
        if (Node.string(node).trim().length === 0) {
          const hasTwin = Array.from(Editor.nodes(editor, { at: [], match: (n) => isTag(n) && n.tagId === node.tagId })).length > 1;
          if (!hasTwin) relinkPredecessor(editor, node.tagId, node.continuedAt);

          Transforms.removeNodes(editor, { at: path });
          return;
        }

        const nextPath = Path.next(path);
        try {
          const [nextNode] = Editor.node(editor, nextPath);
          if (isTag(nextNode) && nextNode.tagId === node.tagId) {
            Transforms.mergeNodes(editor, { at: nextPath });
            return;
          }
        } catch {
          console.warn('Error accessing next node during tag normalization');
        }
      }

      normalizeNode(entry);
    };

    editor.renderElement = (props) => {
      if (isTag(props.element)) {
        return <TagElement {...props} element={props.element} isPDF={isPDF} />;
      }
      return renderElement(props);
    };

    return editor;
  };

export default withTag;
